import { useEffect, useRef, useState } from 'react'

import { Input } from '@/components/Input'
import { getAddress, getDistance } from '@/services'
import {
  Delivery,
  useCakeMakerActions,
  useCakeMakerState,
} from '@/store/useCakeMaker'
import { InteractiveLabel } from '../../components/InteractiveLabel'
import { Resume } from '../../components/Resume'
import { DeliveryTax } from './components/DeliveryTax'

import './styles.scss'

export function CheckoutStep() {
  const { delivery } = useCakeMakerState()
  const { setDelivery } = useCakeMakerActions()
  const [zipCode, setZipCode] = useState('')
  const [number, setNumber] = useState('')
  const [complement, setComplement] = useState('')
  const numberInputRef = useRef<HTMLInputElement>(null)
  const [calculating, setCalculating] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAddress = async () => {
    setCalculating(true)
    setError(null)

    const address = await getAddress(zipCode.replace('-', ''))

    if (!address?.cep) {
      setError('Não encontramos esse CEP. Confira os números e tente de novo.')
      resetDeliveryWith()
      setCalculating(false)
      return
    }

    const { logradouro, bairro, localidade, uf, cep } = address
    const distance = await getDistance(cep)

    if (distance?.error) {
      setError(distance.error)
      setNumber('')
      setComplement('')
      resetDeliveryWith()
      setCalculating(false)
      return
    }

    setDelivery({
      address: logradouro,
      city: localidade,
      state: uf,
      neighborhood: bairro,
      zipCode: cep,
      price: distance?.price ?? 0,
      number,
      complement,
    })
    setCalculating(false)
    numberInputRef.current?.focus()
  }

  const resetAll = () => {
    resetDeliveryWith({ zipCode })
    setZipCode('')
    setNumber('')
    setComplement('')
    setCalculating(false)
    setError(null)
  }

  const resetDeliveryWith = (override?: Partial<Delivery>) => {
    setCalculating(false)
    setDelivery({
      address: '',
      city: '',
      state: '',
      neighborhood: '',
      price: 0,
      number: '',
      complement: '',
      zipCode: '',
      ...override,
    })
  }

  useEffect(() => {
    if (zipCode.length === 9) {
      fetchAddress()
    } else {
      setError(null)
      resetDeliveryWith({ zipCode })
    }
  }, [zipCode])

  useEffect(() => {
    if (delivery) setDelivery({ ...delivery, number, complement })
  }, [number, complement, setDelivery])

  return (
    <div className="checkout-step">
      <div className="checkout-step__item">
        <InteractiveLabel id="checkout-entrega-label">Entrega</InteractiveLabel>

        <div
          className="address-form"
          role="group"
          aria-labelledby="checkout-entrega-label"
        >
          <Input
            id="checkout-cep"
            name="cep"
            label="CEP"
            placeholder="CEP"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            mask="zipCode"
            aria-required
            aria-invalid={!!error}
            aria-describedby={error ? 'checkout-cep-error' : undefined}
          />

          <Input
            ref={numberInputRef}
            id="checkout-numero"
            name="numero"
            label="Número"
            placeholder={`Número ${zipCode.length === 9 ? `*` : ''}`}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            maxLength={15}
            autoComplete="address-line2"
            aria-required={zipCode.length === 9}
          />

          <Input
            id="checkout-complemento"
            name="complemento"
            label="Complemento (opcional)"
            placeholder="Complemento"
            value={complement}
            onChange={(e) => setComplement(e.target.value)}
            autoComplete="address-line3"
          />
        </div>

        {error && (
          <p
            className="checkout-step__error"
            id="checkout-cep-error"
            role="alert"
          >
            {error}
          </p>
        )}

        <DeliveryTax
          delivery={delivery}
          calculating={calculating}
          onRemove={resetAll}
        />
      </div>

      <div className="checkout-step__item">
        <InteractiveLabel>Resumo</InteractiveLabel>
        <Resume />
      </div>
    </div>
  )
}
